"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PathEvaluator = void 0;
/**
 * PathEvaluator - Utility class for flexible path evaluation in nested objects
 * Supports: nested.path, array[0].path, array.path (all elements), custom fields
 */
class PathEvaluator {
    /**
     * Extract value from object using flexible path notation
     * Supports: nested.path, array[0].path, array.path (all elements)
     *
     * @param obj - The object to extract value from
     * @param path - Dot notation path (e.g., "groups.name", "homeLocation.city", "groups[0].name")
     * @returns The extracted value(s) or null if not found
     */
    static extractValue(obj, path) {
        if (!obj || !path) {
            return null;
        }
        try {
            const segments = this.parsePath(path);
            return this.traversePath(obj, segments);
        }
        catch (error) {
            console.warn(`Error extracting value for path "${path}":`, error);
            return null;
        }
    }
    /**
     * Check if path exists and has a meaningful value in object
     *
     * @param obj - The object to check
     * @param path - Dot notation path
     * @returns true if path exists and has a non-null, non-empty value
     */
    static hasValue(obj, path) {
        const value = this.extractValue(obj, path);
        if (value === null || value === undefined) {
            return false;
        }
        // Handle arrays
        if (Array.isArray(value)) {
            return (value.length > 0 && value.some((item) => this.isNonEmptyValue(item)));
        }
        return this.isNonEmptyValue(value);
    }
    /**
     * Get all unique values for a path across array of objects
     *
     * @param objects - Array of objects to extract values from
     * @param path - Dot notation path
     * @returns Array of unique values found across all objects
     */
    static extractAllValues(objects, path) {
        if (!Array.isArray(objects) || !path) {
            return [];
        }
        const allValues = new Set();
        for (const obj of objects) {
            const value = this.extractValue(obj, path);
            if (value !== null && value !== undefined) {
                if (Array.isArray(value)) {
                    // Flatten array values
                    value.forEach((item) => {
                        if (this.isNonEmptyValue(item)) {
                            allValues.add(this.normalizeValue(item));
                        }
                    });
                }
                else if (this.isNonEmptyValue(value)) {
                    allValues.add(this.normalizeValue(value));
                }
            }
        }
        return Array.from(allValues).sort((a, b) => {
            // Sort strings alphabetically, numbers numerically
            if (typeof a === "string" && typeof b === "string") {
                return a.localeCompare(b, undefined, { sensitivity: "base" });
            }
            return String(a).localeCompare(String(b));
        });
    }
    /**
     * Validate that a path is accessible for given object structure
     *
     * @param obj - The object to validate against
     * @param path - Dot notation path
     * @returns true if path is valid and accessible
     */
    static validatePath(obj, path) {
        if (!obj || !path) {
            return false;
        }
        try {
            const segments = this.parsePath(path);
            return this.isPathValid(obj, segments);
        }
        catch (error) {
            return false;
        }
    }
    /**
     * Parse a path string into segments, handling array notation
     *
     * @param path - Path string like "groups.name" or "groups[0].name"
     * @returns Array of path segments with type information
     */
    static parsePath(path) {
        const segments = [];
        // Split on dots but preserve array brackets
        const parts = path.split(".");
        for (const part of parts) {
            if (part.includes("[") && part.includes("]")) {
                // Handle array notation like "groups[0]" or "groups[0]"
                const arrayMatch = part.match(/^([^[]+)\[(\d+)\]$/);
                if (arrayMatch) {
                    segments.push({
                        key: arrayMatch[1],
                        type: "array-indexed",
                        index: parseInt(arrayMatch[2], 10),
                    });
                }
                else {
                    // Invalid array notation - create a path that will result in null
                    // by using a non-existent property name
                    segments.push({
                        key: `__invalid_${part}__`,
                        type: "property",
                    });
                }
            }
            else {
                segments.push({
                    key: part,
                    type: "property",
                });
            }
        }
        return segments;
    }
    /**
     * Traverse object following path segments
     *
     * @param obj - Current object to traverse
     * @param segments - Remaining path segments
     * @returns The value found at the end of the path
     */
    static traversePath(obj, segments) {
        let current = obj;
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            const isLastSegment = i === segments.length - 1;
            if (current === null || current === undefined) {
                return null;
            }
            if (segment.type === "array-indexed") {
                // Handle array[index] notation
                if (!Array.isArray(current[segment.key])) {
                    return null;
                }
                const array = current[segment.key];
                if (segment.index === undefined || segment.index >= array.length) {
                    return null;
                }
                current = array[segment.index];
            }
            else {
                // Handle regular property access
                if (Array.isArray(current[segment.key]) && !isLastSegment) {
                    // If we encounter an array but there are more segments,
                    // we need to extract the next property from all array elements
                    const remainingSegments = segments.slice(i + 1);
                    const results = [];
                    for (const item of current[segment.key]) {
                        const value = this.traversePath(item, remainingSegments);
                        if (value !== null && value !== undefined) {
                            if (Array.isArray(value)) {
                                results.push(...value);
                            }
                            else {
                                results.push(value);
                            }
                        }
                    }
                    return results.length > 0 ? results : null;
                }
                else {
                    // Check if property exists to return null instead of undefined
                    if (!Object.prototype.hasOwnProperty.call(current, segment.key)) {
                        return null;
                    }
                    current = current[segment.key];
                }
            }
        }
        return current === undefined ? null : current;
    }
    /**
     * Check if path is valid by traversing without extracting values
     *
     * @param obj - Object to validate against
     * @param segments - Path segments to validate
     * @returns true if path is structurally valid
     */
    static isPathValid(obj, segments) {
        let current = obj;
        for (const segment of segments) {
            if (current === null || current === undefined) {
                return false;
            }
            if (segment.type === "array-indexed") {
                // Check if property exists and is array
                if (!Object.prototype.hasOwnProperty.call(current, segment.key) ||
                    !Array.isArray(current[segment.key])) {
                    return false;
                }
                const array = current[segment.key];
                if (segment.index === undefined || segment.index >= array.length) {
                    return false;
                }
                current = array[segment.index];
            }
            else {
                // Check if property exists
                if (!Object.prototype.hasOwnProperty.call(current, segment.key)) {
                    return false;
                }
                current = current[segment.key];
            }
        }
        return true;
    }
    /**
     * Check if a value is non-empty and meaningful
     *
     * @param value - Value to check
     * @returns true if value is meaningful (not null, undefined, empty string)
     */
    static isNonEmptyValue(value) {
        if (value === null || value === undefined) {
            return false;
        }
        if (typeof value === "string") {
            return value.trim().length > 0;
        }
        if (typeof value === "number") {
            return !isNaN(value);
        }
        if (typeof value === "boolean") {
            return true;
        }
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        if (typeof value === "object") {
            return Object.keys(value).length > 0;
        }
        return true;
    }
    /**
     * Normalize value for comparison and storage
     *
     * @param value - Value to normalize
     * @returns Normalized string representation
     */
    static normalizeValue(value) {
        if (typeof value === "string") {
            return value.trim();
        }
        if (typeof value === "object" && value !== null) {
            // For objects, try to get a meaningful string representation
            if (value.name)
                return String(value.name);
            if (value.id)
                return String(value.id);
            if (value.title)
                return String(value.title);
            return JSON.stringify(value);
        }
        return String(value);
    }
}
exports.PathEvaluator = PathEvaluator;
